import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  ContactImportBirthdayFormat,
  ContactImportColumnMapping,
  ContactImportMappedTarget,
} from "@multi-chat/shared";
import {
  CONTACT_IMPORT_BIRTHDAY_FORMATS,
  CONTACT_IMPORT_COLUMN_TARGETS,
  resolveContactImportBirthdayColumn,
} from "@multi-chat/shared";
import { SimpleSelect } from "../../../components/ui/select/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";

const MAPPING_GRID =
  "sm:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_13.5rem]";

const IDENTITY_IMPORT_HINTS = {
  phone: {
    bodyKey: "contacts.import.mapping.phoneHint",
    labelKey: "contacts.import.mapping.phoneHintLabel",
    testId: "import-map-phone-hint",
  },
  email: {
    bodyKey: "contacts.import.mapping.emailHint",
    labelKey: "contacts.import.mapping.emailHintLabel",
    testId: "import-map-email-hint",
  },
  birthday: {
    bodyKey: "contacts.import.mapping.birthdayHelp",
    labelKey: "contacts.import.mapping.birthdayHintLabel",
    testId: "import-map-birthday-hint",
  },
} as const;

function identityImportHint(target: ContactImportMappedTarget) {
  switch (target) {
    case "phone":
    case "email":
    case "birthday":
      return IDENTITY_IMPORT_HINTS[target];
    case "firstName":
    case "lastName":
    case "displayName":
    case "groups":
    case "skip":
      return null;
    default: {
      const exhaustive: never = target;
      return exhaustive;
    }
  }
}

function MappingIdentityHint({
  target,
}: {
  target: ContactImportMappedTarget;
}): React.JSX.Element {
  const { t } = useTranslation();
  const hint = identityImportHint(target);
  if (!hint) {
    return <span className="size-8 shrink-0" aria-hidden="true" />;
  }
  return (
    <SimpleTooltip content={t(hint.bodyKey)}>
      <button
        type="button"
        className="inline-grid size-8 shrink-0 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]"
        aria-label={t(hint.labelKey)}
        data-testid={hint.testId}
      >
        <Info size={15} aria-hidden="true" />
      </button>
    </SimpleTooltip>
  );
}

export function ImportContactsMappingStep({
  headers,
  samples,
  sampleRows,
  mapping,
  onChangeMapping,
  birthdayFormat,
  onChangeBirthdayFormat,
  birthdayAmbiguous,
  disabled,
}: {
  headers: string[];
  samples: string[];
  sampleRows: string[][];
  mapping: ContactImportColumnMapping;
  onChangeMapping: (next: ContactImportColumnMapping) => void;
  birthdayFormat: ContactImportBirthdayFormat;
  onChangeBirthdayFormat: (next: ContactImportBirthdayFormat) => void;
  birthdayAmbiguous: boolean;
  disabled?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  const targetOptions = [
    { value: "skip", label: t("contacts.import.mapping.skip") },
    ...CONTACT_IMPORT_COLUMN_TARGETS.map((target) => ({
      value: target,
      label: t(`contacts.import.mapping.targets.${target}`),
    })),
  ];
  const birthdayFormatOptions = CONTACT_IMPORT_BIRTHDAY_FORMATS.map((format) => ({
    value: format,
    label: t(`contacts.import.mapping.formats.${format}`),
  }));
  const birthdayColumnIndex = mapping.findIndex((entry) => entry.target === "birthday");
  const birthdaySamples =
    birthdayColumnIndex >= 0
      ? sampleRows.map((row) => row[birthdayColumnIndex] ?? "")
      : [];
  const sampleSkipped =
    birthdaySamples.length > 0 &&
    resolveContactImportBirthdayColumn(birthdaySamples, birthdayFormat).birthdays.some(
      (cell) => cell.skipped,
    );

  function handleTargetChange(source: string, target: ContactImportMappedTarget): void {
    onChangeMapping(
      mapping.map((entry) => {
        if (entry.source === source) return { ...entry, target };
        if (target !== "skip" && entry.target === target) return { ...entry, target: "skip" };
        return entry;
      }),
    );
  }

  return (
    <div className="grid gap-2">
      <div className="grid gap-0.5">
        <p className="m-0 text-[0.8125rem] font-medium text-foreground">
          {t("contacts.import.mapping.title")}
        </p>
        <p className="m-0 text-[0.75rem] leading-snug text-muted">
          {t("contacts.import.mapping.help")}
        </p>
      </div>
      <div
        className={cn(
          "hidden border-b border-line pb-1 text-[0.6875rem] font-medium text-muted sm:grid sm:gap-x-3",
          MAPPING_GRID,
        )}
      >
        <span>{t("contacts.import.mapping.column")}</span>
        <span>{t("contacts.import.mapping.sample")}</span>
        <span>{t("contacts.import.mapping.field")}</span>
      </div>
      <div>
        {mapping.map((entry, index) => {
          const skipped = entry.target === "skip";
          const birthdayRow = entry.target === "birthday";
          return (
            <div
              key={entry.source}
              className={cn(
                "grid gap-1.5 border-b border-border/70 py-2 sm:items-center sm:gap-x-3",
                MAPPING_GRID,
                skipped && "text-muted",
              )}
              data-testid={`import-map-row-${index}`}
            >
              <p className="m-0 truncate text-[0.8125rem] font-medium text-foreground">
                {headers[index] ?? entry.source}
              </p>
              <p className="m-0 truncate text-[0.75rem] text-muted">
                {samples[index] ? (
                  <LtrValue title={samples[index]}>{samples[index]}</LtrValue>
                ) : (
                  t("contacts.import.mapping.noSample")
                )}
              </p>
              <div className="flex min-w-0 items-center gap-1">
                <SimpleSelect
                  value={entry.target}
                  onValueChange={(value) =>
                    handleTargetChange(entry.source, value as ContactImportMappedTarget)
                  }
                  options={targetOptions}
                  ariaLabel={t("contacts.import.mapping.targetLabel", { column: entry.source })}
                  disabled={disabled}
                  triggerClassName="h-8 min-w-0 flex-1 text-[0.8125rem]"
                />
                <MappingIdentityHint target={entry.target} />
              </div>
              {birthdayRow ? (
                <>
                  <div
                    className="flex min-w-0 items-center gap-1 sm:col-start-3 sm:max-w-[13.5rem]"
                    data-testid="import-birthday-format"
                  >
                    <SimpleSelect
                      value={birthdayFormat}
                      onValueChange={(value) =>
                        onChangeBirthdayFormat(value as ContactImportBirthdayFormat)
                      }
                      options={birthdayFormatOptions}
                      ariaLabel={t("contacts.import.mapping.birthdayFormatLabel")}
                      disabled={disabled}
                      triggerClassName="h-8 min-w-0 flex-1 text-[0.8125rem]"
                    />
                    <span className="size-8 shrink-0" aria-hidden="true" />
                  </div>
                  {birthdayAmbiguous && birthdayFormat === "auto" ? (
                    <p
                      className="m-0 text-[0.75rem] leading-snug text-warning sm:col-span-3"
                      data-testid="import-birthday-format-required"
                    >
                      {t("contacts.import.mapping.birthdayFormatRequired")}
                    </p>
                  ) : null}
                  {sampleSkipped ? (
                    <p className="m-0 text-[0.75rem] leading-snug text-muted sm:col-span-3">
                      {t("contacts.import.mapping.birthdaySampleSkipped")}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
